// Core packages
import { AbstractControl } from '@angular/forms';

// Third party packages
import { asapScheduler, Subscription } from 'rxjs';
import { filter, observeOn, pairwise, startWith } from 'rxjs/operators';

/**
 * "Servizi aggiuntivi" of a company's contractual conditions.
 *
 * Each service is enabled by its own `subscription.<flag>` and priced by
 * `subscription.<flag>Fee`; they are independent of `documentUpload` and
 * drive which sections the Master sees in "my company".
 *
 * @since 3.0.0
 */
export const ADDITIONAL_SERVICE_FLAGS: string[] = [
  'safetyVideoResidents',
  'safetyVideoSuppliers',
  'fireSafety',
  'informativeNote',
];

/**
 * Services that used to be enabled all together by the single legacy
 * `subscription.additionalServices` flag (priced by `additionalServicesFee`).
 * While a company has never saved its own value for one of them, that service
 * inherits the legacy flag (lazy migration: the first contract save makes it explicit).
 */
export const LEGACY_ADDITIONAL_SERVICE_FLAGS: string[] = [
  'safetyVideoResidents',
  'safetyVideoSuppliers',
  'fireSafety',
];

/**
 * Services pre-selected when a new company is created with the document upload
 * active (the default). The "Nota informativa" is never pre-selected.
 */
export const DEFAULT_ADDITIONAL_SERVICE_FLAGS: string[] = [
  'safetyVideoResidents',
  'safetyVideoSuppliers',
  'fireSafety',
];

/**
 * Services each service requires: they are turned on together with it and cannot
 * be turned off while it is on. The "Nota informativa" holds the QR codes of every
 * additional service and the condo-owner privacy access key.
 * Mirrored on the back-end by utils/subscription-service-links.ts.
 */
export const SERVICE_REQUIREMENTS: { [service: string]: string[] } = {
  informativeNote: ['safetyVideoResidents', 'safetyVideoSuppliers', 'fireSafety', 'privacy'],
  suppliers: ['safetyVideoSuppliers'],
};

/** Services that cannot be turned on while the given flags are on */
export const SERVICE_EXCLUSIONS: { [service: string]: string[] } = {
  informativeNote: ['privacySag'],
};

/** Why a change of a linked service flag was refused */
export type ServiceLinkRefusal = 'requiredByActiveService' | 'excludedByActiveService';

/** Loosely typed contractual conditions, as returned by the API */
export type SubscriptionRecord = { [key: string]: unknown } | null | undefined;

/**
 * Whether an additional service is active for the given contractual conditions:
 * an explicitly saved flag wins; a never-saved legacy service inherits
 * `additionalServices`; anything else is off.
 *
 * @since 3.0.0
 *
 * @param subscription Company contractual conditions
 * @param flag Service flag key
 * @returns true when the service is active
 */
export function isAdditionalServiceActive(subscription: SubscriptionRecord, flag: string): boolean {
  if (!subscription) {
    return false;
  }
  const value = subscription[flag];
  if (value === true || value === false) {
    return value;
  }
  return LEGACY_ADDITIONAL_SERVICE_FLAGS.indexOf(flag) > -1 && subscription.additionalServices === true;
}

/**
 * Fee to show for an additional service: the saved one when present, otherwise
 * (company saved before the split) the legacy single `additionalServicesFee`
 * for the services it used to cover, otherwise 0.
 *
 * @since 3.0.0
 *
 * @param subscription Company contractual conditions
 * @param flag Service flag key (its fee is stored in `${flag}Fee`)
 * @returns Fee value
 */
export function additionalServiceFee(subscription: SubscriptionRecord, flag: string): number {
  if (!subscription) {
    return 0;
  }
  const saved = subscription[`${flag}Fee`];
  if (typeof saved === 'number' && isFinite(saved)) {
    return saved;
  }
  const legacy = subscription.additionalServicesFee;
  if (LEGACY_ADDITIONAL_SERVICE_FLAGS.indexOf(flag) > -1 && typeof legacy === 'number' && isFinite(legacy)) {
    return legacy;
  }
  return 0;
}

/** Every subscription flag involved in a service link */
const LINKED_SERVICE_FLAGS: string[] = Object.keys(SERVICE_REQUIREMENTS)
  .concat(...Object.keys(SERVICE_REQUIREMENTS).map((service) => SERVICE_REQUIREMENTS[service]))
  .concat(...Object.keys(SERVICE_EXCLUSIONS).map((service) => SERVICE_EXCLUSIONS[service]))
  .filter((flag, index, flags) => flags.indexOf(flag) === index);

/**
 * Active services that require the given flag (e.g. "privacy" is required by an
 * active "Nota informativa"): while this list is not empty the flag cannot be turned off.
 *
 * @since 3.0.0
 *
 * @param subscription Current contractual conditions (form value)
 * @param flag Subscription flag key
 * @returns Keys of the active services requiring the flag
 */
export function activeServicesRequiring(subscription: SubscriptionRecord, flag: string): string[] {
  return Object.keys(SERVICE_REQUIREMENTS).filter(
    (service) => SERVICE_REQUIREMENTS[service].indexOf(flag) > -1 && isAdditionalServiceActive(subscription, service)
  );
}

/**
 * Active flags that forbid turning the given service on (e.g. "privacySag" for the
 * "Nota informativa", which requires the standard privacy).
 *
 * @since 3.0.0
 *
 * @param subscription Current contractual conditions (form value)
 * @param service Subscription flag key of the service being turned on
 * @returns Keys of the active flags excluding the service
 */
export function activeExclusions(subscription: SubscriptionRecord, service: string): string[] {
  const exclusions = SERVICE_EXCLUSIONS[service] || [];
  return exclusions.filter((flag) => isAdditionalServiceActive(subscription, flag));
}

/**
 * Snapshot of the linked flags of a subscription form group. Read control by control
 * so disabled controls (e.g. "privacySag" while "privacy" is on) are included.
 */
function readLinkedFlags(group: AbstractControl): { [flag: string]: unknown } {
  const values: { [flag: string]: unknown } = {};
  for (const flag of LINKED_SERVICE_FLAGS) {
    const control = group.get(flag);
    if (control) {
      values[flag] = control.value;
    }
  }
  return values;
}

/**
 * Enforce the links between services on the contract form, for user changes only
 * (programmatic changes, e.g. loading the company, leave the control pristine):
 *  - turning a service on turns on the services it requires, unless an excluding flag
 *    is active: then the change is reverted and refused;
 *  - turning a service off while an active service requires it is reverted and refused.
 * Each check runs once the current change has reached every other listener (asap
 * scheduler), so reverting cannot interleave with their emissions.
 *
 * @since 3.0.0
 *
 * @param group Subscription form group ("subscription" of the contract form)
 * @param onRefused Called after a refused change has been reverted
 * @param onError Called on an unexpected stream error
 * @returns Subscriptions to release on destroy
 */
export function bindServiceLinks(
  group: AbstractControl,
  onRefused: (refusal: ServiceLinkRefusal) => void,
  onError: (err: unknown) => void
): Subscription[] {
  const subscriptions: Subscription[] = [];
  for (const flag of LINKED_SERVICE_FLAGS) {
    const control = group.get(flag);
    if (!control) {
      continue;
    }
    subscriptions.push(
      control.valueChanges
        .pipe(
          startWith(control.value),
          pairwise(),
          filter(([previous, current]) => (previous === true) !== (current === true) && control.dirty),
          observeOn(asapScheduler)
        )
        .subscribe(
          ([, current]) => {
            const turnedOn = current === true;
            // Superseded by a later change: that change is checked on its own
            if ((control.value === true) !== turnedOn) {
              return;
            }
            const values = readLinkedFlags(group);
            if (turnedOn) {
              if (activeExclusions(values, flag).length) {
                control.setValue(false);
                onRefused('excludedByActiveService');
                return;
              }
              for (const required of SERVICE_REQUIREMENTS[flag] || []) {
                const requiredControl = group.get(required);
                if (requiredControl && requiredControl.value !== true) {
                  requiredControl.setValue(true);
                }
              }
              return;
            }
            if (activeServicesRequiring(values, flag).length) {
              control.setValue(true);
              onRefused('requiredByActiveService');
            }
          },
          onError
        )
    );
  }
  return subscriptions;
}
